import './ClaimPhoneNumberModal.scss'
import React, { ChangeEvent, useEffect } from 'react'
import { Modal } from '../../../../../components/Modal/Modal'
import { Alert, Col, Container, Form, Row, Spinner, Stack } from 'react-bootstrap'
import { claimPhoneNumberDefaultValues, ClaimPhoneNumberFormValues, ClaimPhoneNumberPage } from './models'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Button } from '../../../../../components/Button/Button'
import { useClaimExternalUserMutation, useCreateUserIntegrationMutation, useGetAllServiceLocationsQuery } from '../../../../../store/user/user'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { PhoneNumber } from '../../../../../components/PhoneNumber/PhoneNumber'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { ContactCardSelector } from '../../../../../components/ContactCardSelector/ContactCardSelector'
import 'react-phone-number-input/style.css'
import PhoneInputWithCountry from 'react-phone-number-input/react-hook-form'
import { isPossiblePhoneNumber } from 'react-phone-number-input'

export type ClaimPhoneNumberModalProps = {
  show: boolean
  onHide: () => void
  page?: ClaimPhoneNumberPage
  phoneNumber?: string
}

export type CompletePageFooterProps = {
  onHide: () => void
}

const validatePhoneNumber = (value: string) => {
  return isPossiblePhoneNumber(value) || 'Invalid number'
}

const EnterPhoneNumberPageBody: React.FC = () => {
  const { control } = useFormContext<ClaimPhoneNumberFormValues>()

  return (
    <Container fluid>
      <Row className='pb-3'>
        <Col className='px-1'>
          <h4 className='d-flex justify-content-center align-items-center'>Enter your phone number</h4>
        </Col>
      </Row>
      <Row className='pb-4'>
        <Col className='px-1'>
          <div className='paragraph-medium'>
            Enter your country code and phone number to get 6-digit verification code. Supports mobile and landline phone numbers.
          </div>
        </Col>
      </Row>
      <Row className='pb-5'>
        <Col className='px-1'>
          <Controller
            name='phoneNumber'
            control={control}
            rules={{
              required: 'Required field',
              validate: { validatePhoneNumber },
            }}
            render={({ field, fieldState }) => (
              <Form.Group className='mb-3' controlId='phoneNumber'>
                <Form.Label>Phone number*</Form.Label>
                <PhoneInputWithCountry
                  autoFocus
                  placeholder='(800) 555-0175'
                  defaultCountry='US'
                  countryOptionsOrder={['US', 'CA', '...']}
                  international={true}
                  inputComponent={Form.Control}
                  type='tel'
                  isInvalid={!!fieldState.error}
                  aria-label='Enter phone number to claim'
                  {...field}
                ></PhoneInputWithCountry>
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </Form.Group>
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col className='px-1'>
          <Controller
            name='verificationChannel'
            control={control}
            render={({ field, fieldState }) => {
              return (
                <>
                  <div className='paragraph-medium pb-3'>How would you prefer to receive your code?</div>
                  <div>
                    <Form.Check
                      inline
                      type='radio'
                      className='pb-2'
                      label='Text message (SMS)'
                      id='sms-verificationChannel'
                      {...field}
                      value='sms'
                      checked={field.value === 'sms'}
                    />
                    <Form.Check inline type='radio' label='Voice call' id='call-verificationChannel' {...field} value='call' checked={field.value === 'call'} />
                  </div>
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </>
              )
            }}
          />
        </Col>
      </Row>
    </Container>
  )
}

const EnterPhoneNumberPageFooter: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { setValue, getValues, trigger } = useFormContext<ClaimPhoneNumberFormValues>()
  const [claimExternalUser, { isLoading: isSending, isSuccess: isSendSuccess }] = useClaimExternalUserMutation()
  const handleSendCode = async () => {
    const valid = await trigger('phoneNumber')
    if (valid) {
      const phoneNumber = getValues('phoneNumber')
      const verificationChannel = getValues('verificationChannel')
      claimExternalUser({
        accountId: accountId,
        integrationName: IntegrationServiceId.PHONE,
        payload: { externalAccountId: '', externalId: phoneNumber, verificationChannel },
      })
    }
  }

  useEffect(() => {
    if (isSendSuccess) {
      setValue('page', ClaimPhoneNumberPage.VERIFICATION)
    }
  }, [isSendSuccess, setValue])

  return (
    <Container fluid className='d-flex flex-column gap-4 p-0'>
      <Row>
        <Col className='d-flex align-items-center justify-content-center'>
          <Button variant={'primary'} className='w-100 label-medium bold' onClick={handleSendCode} disabled={isSending} loading={isSending}>
            Continue
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className='paragraph-small'>
            Your phone number will remain private and will not be shared or used for marketing purposes.{' '}
            <a target='_blank' href='https://www.zocks.io/privacy-policy' rel='noreferrer' className='button-link-small zocks-text-gray-900'>
              Privacy Policy
            </a>
          </div>
        </Col>
      </Row>
    </Container>
  )
}

const VerifyPageBody: React.FC = () => {
  const { setValue, getValues, control, register, setFocus, clearErrors } = useFormContext<ClaimPhoneNumberFormValues>()
  const update = (index: number) => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value
      clearErrors('verificationCode')
      const segments = getValues('verificationCode')
        .split('')
        .map((s: string) => (s === ' ' ? '' : s))
      const newAdditions = newValue ? newValue.split('') : ['']
      const newSegments = [...segments.slice(0, index), ...newAdditions, ...segments.slice(index + newAdditions.length)].slice(0, segments.length)
      setValue('segments', newSegments)
      const newVerificationCode = newSegments.map((s) => (s === '' ? ' ' : s)).join('')
      setValue('verificationCode', newVerificationCode)
      const lastDigitIndex = index + (newValue.length ? newValue.length : -1)
      if (lastDigitIndex >= 0 && lastDigitIndex < newSegments.length) {
        setFocus(`segments.${lastDigitIndex}`)
      }
    }
  }

  const handleChange = () => {
    setValue('page', ClaimPhoneNumberPage.PHONE_NUMBER)
  }

  const phoneNumber = getValues('phoneNumber')
  return (
    <Container fluid className='d-flex flex-column gap-3'>
      <Row>
        <Col className='px-1'>
          <h4 className='d-flex justify-content-center align-items-center'>Enter verification code</h4>
        </Col>
      </Row>
      <Row>
        <Col className='p-3 zocks-bg-color-gray-100 zocks-border-0.5'>
          <Stack gap={2}>
            <div className='lead-small'>We've sent a code to</div>
            <Stack direction='horizontal' gap={2}>
              <div className='paragraph-medium'>
                <PhoneNumber phoneNumber={phoneNumber} />
              </div>
              <div role='button' className='ms-auto button-link-small' onClick={handleChange}>
                Change
              </div>
            </Stack>
          </Stack>
        </Col>
      </Row>
      <Row className='pt-2'>
        <Col className='px-1'>
          <Controller
            name='verificationCode'
            control={control}
            rules={{
              required: 'Required field',
              pattern: {
                value: /\d{6}/,
                message: 'Invalid field',
              },
            }}
            render={({ field, fieldState }) => (
              <Form.Group controlId='verificationCode'>
                <Form.Label>Enter the 6-digit code*</Form.Label>
                <Stack direction='horizontal' gap={2}>
                  {field.value.split('').map((s: string, key: number) => (
                    <Form.Control
                      type='number'
                      min={0}
                      max={9}
                      key={key}
                      {...register(`segments.${key}`)}
                      maxLength={1}
                      isInvalid={!!fieldState.error}
                      autoFocus={key === 0}
                      onInput={update(key)}
                      className='verification-code-digit label-large align-items-center justify-content-center text-center'
                    />
                  ))}
                </Stack>
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </Form.Group>
            )}
          />
        </Col>
      </Row>
    </Container>
  )
}

const VerifyPageFooter: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { setValue, getValues, trigger } = useFormContext<ClaimPhoneNumberFormValues>()
  const [createUserIntegration, { isLoading: isCreating, isSuccess: isCreateSuccess }] = useCreateUserIntegrationMutation()
  const handleCreateIntegration = async () => {
    const valid = await trigger('verificationCode')
    if (valid) {
      const phoneNumber = getValues('phoneNumber')
      const verificationCode = getValues('verificationCode')
      createUserIntegration({
        accountId,
        integrationName: IntegrationServiceId.PHONE,
        payload: { externalAccountId: '', externalId: phoneNumber, verificationCode },
      })
    }
  }

  useEffect(() => {
    if (isCreateSuccess) {
      setValue('page', ClaimPhoneNumberPage.COMPLETE)
    }
  }, [isCreateSuccess, setValue])

  return (
    <Container fluid className='d-flex flex-column gap-4 p-0'>
      <Row>
        <Col className='d-flex align-items-center justify-content-center'>
          <Button variant={'primary'} className='w-100 label-medium bold' onClick={handleCreateIntegration} disabled={isCreating} loading={isCreating}>
            Submit code
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className='paragraph-small'>If you did not receive the code, check that you have entered the right number, and try again.</div>
        </Col>
      </Row>
    </Container>
  )
}

const CompletePageBody: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: locations, isFetching: locationsFetching } = useGetAllServiceLocationsQuery({
    accountId: accountId,
    serviceId: IntegrationServiceId.PHONE,
  })

  return (
    <Container fluid className='d-flex flex-column gap-4'>
      <Row className='pb-3'>
        <Col className='px-1'>
          <h4 className='d-flex justify-content-center align-items-center'>Verification Completed</h4>
        </Col>
      </Row>
      <Row>
        <Col className='px-0'>
          <div className='paragraph-medium'>
            Your phone number has been successfully validated. All you have to do is save <strong>Zocks contact card</strong> in your phonebook and invite it to
            your next business call.
          </div>
        </Col>
      </Row>
      <Row>
        <Col className='px-1'>
          {locationsFetching ? (
            <div className='d-flex justify-content-center'>
              <Spinner />
            </div>
          ) : locations?.data.length ? (
            <ContactCardSelector locations={locations.data} />
          ) : (
            <Alert variant='danger'>
              <Alert.Heading>Oh snap! You got an error!</Alert.Heading>
              <p>Unable to get any locations. Please contact us.</p>
            </Alert>
          )}
        </Col>
      </Row>
      <Row>
        <Col className='px-0'>
          <div className='paragraph-medium'>
            If you would like to learn more, you can read our full{' '}
            <a
              target='_blank'
              href='https://help.zocks.io/en/articles/9969795-how-to-connect-zocks-to-your-phone-calls'
              rel='noreferrer'
              className='button-link-medium zocks-text-gray-900'
            >
              Help&nbsp;article.
            </a>
          </div>
        </Col>
      </Row>
    </Container>
  )
}

const CompletePageFooter: React.FC<CompletePageFooterProps> = ({ onHide }) => {
  return (
    <Container fluid className='d-flex flex-column gap-4 p-0'>
      <Row>
        <Col className='d-flex align-items-center justify-content-center'>
          <Button variant={'primary'} className='w-100 label-medium bold' onClick={onHide}>
            Close
          </Button>
        </Col>
      </Row>
    </Container>
  )
}

export const ClaimPhoneNumberModal: React.FC<ClaimPhoneNumberModalProps> = ({ show, onHide, page, phoneNumber }) => {
  const defaultValues = claimPhoneNumberDefaultValues
  if (page !== undefined) {
    defaultValues.page = page
  }
  if (phoneNumber !== undefined) {
    defaultValues.phoneNumber = phoneNumber
  }
  const form = useForm<ClaimPhoneNumberFormValues>({ defaultValues: claimPhoneNumberDefaultValues as ClaimPhoneNumberFormValues })

  const pageBody = () => {
    const page = form.watch('page')
    switch (page) {
      case ClaimPhoneNumberPage.PHONE_NUMBER:
        return <EnterPhoneNumberPageBody />
      case ClaimPhoneNumberPage.VERIFICATION:
        return <VerifyPageBody />
      case ClaimPhoneNumberPage.COMPLETE:
        return <CompletePageBody />
      default:
        return <></>
    }
  }

  const pageFooter = () => {
    const page = form.watch('page')
    switch (page) {
      case ClaimPhoneNumberPage.PHONE_NUMBER:
        return <EnterPhoneNumberPageFooter />
      case ClaimPhoneNumberPage.VERIFICATION:
        return <VerifyPageFooter />
      case ClaimPhoneNumberPage.COMPLETE:
        return <CompletePageFooter onHide={onHide} />
      default:
        return <></>
    }
  }

  const handleOnHide = () => {
    form.reset()
    onHide()
  }

  return (
    <FormProvider {...form}>
      <Modal
        show={show}
        onHide={handleOnHide}
        header={<></>}
        body={pageBody()}
        footer={pageFooter()}
        centered={true}
        className='claim-phone-number-modal'
        autoFocus={false}
      />
    </FormProvider>
  )
}
